import { Reducer } from 'redux';
import { update } from '../helpers/immutabilityHelper'
import * as Dtos from '../dtos/Tmd.dtos';

import {
    ACTION_MONITORINGDOCUMENT_LOAD_BY_ID,
    ACTION_MONITORINGDOCUMENTS_LOAD_BY_MONITORING_ID,
    ACTION_MONITORINGDOCUMENT_LOAD_FAILURE,
    ACTION_MONITORINGDOCUMENT_LOAD_SUCCESS,

    ACTION_MONITORINGDOCUMENT_SAVE,
    ACTION_MONITORINGDOCUMENT_SAVE_SUCCESS,
    ACTION_MONITORINGDOCUMENT_SAVE_FAILURE,

    ACTION_MONITORINGDOCUMENT_CREATE,
    ACTION_MONITORINGDOCUMENT_CREATE_SUCCESS,
    ACTION_MONITORINGDOCUMENT_CREATE_FAILURE,

    ACTION_MONITORINGDOCUMENT_VALIDATE,
    ACTION_MONITORINGDOCUMENT_VALIDATE_SUCCESS,
    ACTION_MONITORINGDOCUMENT_VALIDATE_FAILURE,

    ACTION_MONITORINGDOCUMENT_CLEAR,
    ACTION_MONITORINGDOCUMENT_FILTER,

    MonitoringDocumentAction,
} from '../actions/monitoringDocument';
import { RequestState } from "../enumerations/RequestState";
import { IRequestState, IFormFilter } from "./index";
import { updateFailureIndexToId } from "../helpers/formErrorHelper";

export interface IMonitoringDocumentState {
    formData: Dtos.MonitoringDocument | Dtos.MonitoringDocument[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    validationFailures: Dtos.ResponseError[];
    lookups: Dtos.Lookup[],
    loadState: IRequestState;
    saveState: IRequestState;
    updateState: IRequestState;
    createState: IRequestState;
    validateState: IRequestState;
    formFilter: IFormFilter;
    formFilteredData: Dtos.MonitoringDocument[];
    formLength: number;
}

const initialState: IMonitoringDocumentState = {
    formData: undefined,
    formState: undefined,
    formProperties: undefined,
    validationFailures: undefined,
    lookups: undefined,
    loadState: {
        status: RequestState.None
    } as IRequestState,
    saveState: {
        status: RequestState.None
    } as IRequestState,
    updateState: {
        status: RequestState.None
    } as IRequestState,
    createState: {
        status: RequestState.None
    } as IRequestState,
    validateState: {
        status: RequestState.None
    } as IRequestState,
    formFilter: {
        search: "",
        showInActive: false
    },
    formFilteredData: [],
    formLength: 0
};


function loading(state: IMonitoringDocumentState, _isLoading: boolean): IMonitoringDocumentState {
    const newState: IMonitoringDocumentState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function loadSuccess(
    state: IMonitoringDocumentState,
    formData: Dtos.MonitoringDocument | Dtos.MonitoringDocument[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[],
    responseStatus: Dtos.ResponseStatus
): IMonitoringDocumentState {
    const newState: IMonitoringDocumentState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        loadState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return filter(newState, state.formFilter);
}

function loadFailure(state: IMonitoringDocumentState, responseStatus: Dtos.ResponseStatus): IMonitoringDocumentState {
    const newState: IMonitoringDocumentState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors
            } as IRequestState
        }
    });

    return newState;
}

function filter(state: IMonitoringDocumentState, formFilter: IFormFilter): IMonitoringDocumentState {

    const items: Dtos.MonitoringDocument[] | undefined =
        state.formData instanceof Array ?
            state.formData :
            undefined;

    const formLength = items ? items.length : 0;

    const formFilteredData = items ? items.filter(p => (p.active == true || formFilter.showInActive) &&
        (formFilter.search == "" ||
            p.name.toLowerCase().indexOf(formFilter.search.toLowerCase()) > -1)) : undefined;

    const newState: IMonitoringDocumentState = update(state, {
        formFilter: {
            $set: formFilter
        },
        formFilteredData: {
            $set: formFilteredData
        },
        formLength: {
            $set: formLength
        }
    });

    return newState;
}

function clear(state: IMonitoringDocumentState, propertiesToClear?: string[]): IMonitoringDocumentState {

    if (!propertiesToClear) {
        const newState: IMonitoringDocumentState = update(initialState, {});
        return newState;
    }

    let stateModifier: any = {};

    propertiesToClear.forEach(propertyToClear => {
        stateModifier[propertyToClear] = { $set: initialState[propertyToClear] }
    });

    const newState = update(state, stateModifier);

    return newState;
}

function saving(state: IMonitoringDocumentState, _isSaving: boolean): IMonitoringDocumentState {
    const newState: IMonitoringDocumentState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function saveSuccess(
    state: IMonitoringDocumentState,
    formData: Dtos.MonitoringDocument | Dtos.MonitoringDocument[],
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): IMonitoringDocumentState {
    const newState: IMonitoringDocumentState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        saveState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function saveFailure(state: IMonitoringDocumentState, responseStatus: Dtos.ResponseStatus): IMonitoringDocumentState {
    const newState: IMonitoringDocumentState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

function creating(state: IMonitoringDocumentState, _isCreating: boolean): IMonitoringDocumentState {
    const newState: IMonitoringDocumentState = update(state, {
        createState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function createSuccess(
    state: IMonitoringDocumentState,
    formData: Dtos.MonitoringDocument,
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[]
): IMonitoringDocumentState {
    const newState: IMonitoringDocumentState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        createState: {
            $set: {
                status: RequestState.Success
            } as IRequestState
        }
    });

    return newState;
}

function createFailure(state: IMonitoringDocumentState, responseStatus: Dtos.ResponseStatus): IMonitoringDocumentState {
    const newState: IMonitoringDocumentState = update(state, {
        createState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors
            } as IRequestState
        }
    });

    return newState;
}

function validating(state: IMonitoringDocumentState, _isValidating: boolean): IMonitoringDocumentState {
    const newState: IMonitoringDocumentState = update(state, {
        validateState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function validateSuccess(
    state: IMonitoringDocumentState,
    formData: Dtos.MonitoringDocument,
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): IMonitoringDocumentState {
    const newState: IMonitoringDocumentState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        validateState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function validateFailure(state: IMonitoringDocumentState, responseStatus: Dtos.ResponseStatus): IMonitoringDocumentState {
    const newState: IMonitoringDocumentState = update(state, {
        validateState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}


const MonitoringDocumentReducer: Reducer<IMonitoringDocumentState> = (state: IMonitoringDocumentState = initialState, action: MonitoringDocumentAction) => {
    switch (action.type) {
        case ACTION_MONITORINGDOCUMENT_LOAD_BY_ID:
        case ACTION_MONITORINGDOCUMENTS_LOAD_BY_MONITORING_ID:
            return loading(state, true);
        case ACTION_MONITORINGDOCUMENT_LOAD_SUCCESS:
            return loadSuccess(state, action.formData, action.formState, action.formProperties, action.lookups, action.responseStatus);
        case ACTION_MONITORINGDOCUMENT_LOAD_FAILURE:
            return loadFailure(state, action.responseStatus);
        case ACTION_MONITORINGDOCUMENT_CLEAR:
            return clear(state, action.propertiesToClear);
        case ACTION_MONITORINGDOCUMENT_SAVE:
            return saving(state, true);
        case ACTION_MONITORINGDOCUMENT_SAVE_SUCCESS:
            return saveSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_MONITORINGDOCUMENT_SAVE_FAILURE:
            return saveFailure(state, action.responseStatus);
        case ACTION_MONITORINGDOCUMENT_CREATE:
            return creating(state, true);
        case ACTION_MONITORINGDOCUMENT_CREATE_SUCCESS:
            return createSuccess(state, action.formData, action.formState, action.formProperties, action.lookups);
        case ACTION_MONITORINGDOCUMENT_CREATE_FAILURE:
            return createFailure(state, action.responseStatus);
        case ACTION_MONITORINGDOCUMENT_VALIDATE:
            return validating(state, true);
        case ACTION_MONITORINGDOCUMENT_VALIDATE_SUCCESS:
            return validateSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_MONITORINGDOCUMENT_VALIDATE_FAILURE:
            return validateFailure(state, action.responseStatus);
        case ACTION_MONITORINGDOCUMENT_FILTER:
            return filter(state, action.formFilter);
    }

    return state;
}

export default MonitoringDocumentReducer;